/**
 * Line diff for the character version view. A classic LCS table: character
 * descriptions are short enough that O(n*m) is irrelevant, and the alternative
 * — shipping a diff library for one screen — is not worth the dependency.
 */

export type DiffOp = { kind: 'same' | 'add' | 'remove'; text: string };

export function diffLines(before: string, after: string): DiffOp[] {
  const a = before.split('\n');
  const b = after.split('\n');
  const n = a.length;
  const m = b.length;

  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i]![j] = a[i] === b[j] ? lcs[i + 1]![j + 1]! + 1 : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
    }
  }

  const ops: DiffOp[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      ops.push({ kind: 'same', text: a[i]! });
      i++;
      j++;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) {
      ops.push({ kind: 'remove', text: a[i]! });
      i++;
    } else {
      ops.push({ kind: 'add', text: b[j]! });
      j++;
    }
  }
  while (i < n) ops.push({ kind: 'remove', text: a[i++]! });
  while (j < m) ops.push({ kind: 'add', text: b[j++]! });
  return ops;
}

/** Field-level summary, which is what a phone-sized consent screen needs. */
export function diffFields(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  fields: string[],
): Array<{ field: string; before: string; after: string }> {
  const render = (v: unknown) => (Array.isArray(v) ? v.join(', ') : String(v ?? ''));
  return fields
    .filter((f) => render(before[f]) !== render(after[f]))
    .map((f) => ({ field: f, before: render(before[f]), after: render(after[f]) }));
}
